// ---------------------------------------------------------------------------
// WHICH PALETTE AND WHICH DENSITY A SURFACE RENDERS IN.
//
// web/src/design/surfaces.css keys every operational value off two attributes
// on <html>: data-surface and data-theme. This file decides what those two
// attributes say, and it is deliberately pure — no React, no DOM, no
// matchMedia — because it is the file that decides what colour the whole
// application is, and a decision taken inside a component is a decision only a
// browser can check.
//
// THE ONE RULE THAT IS NOT OBVIOUS: A SURFACE'S DEFAULT IS NOT ALWAYS THE
// DEVICE'S. 11-console-design-system.md §1 chose "Control Room — dark, dense"
// for the console as a decision about the product, not a preference to be
// overridden by whatever the operating system happens to be set to. Most
// machines report light, so following the device would put nearly every
// operator into the light variant and make the decision decorative. §8 asks for
// device-following on the WORKER app specifically, and gives the reason: the
// same phone is read outdoors in daylight and at 23:00 in the same week. The
// two surfaces genuinely differ, so this file says so rather than applying one
// policy to both.
//
// (That sentence originally quoted §8's own wording, which names a job title,
// and the taxonomy guard refused the file for it — correctly. It cannot tell
// prose from code and must not try. This is at least the seventh time a comment
// ABOUT a rule has tripped that rule in this repository; there is no prose
// exemption and there must not be one.)
// ---------------------------------------------------------------------------

import type { Surface } from "./navigation";

export type Theme = "light" | "dark";

/**
 * What a person has chosen. `system` means "follow the device", and is the
 * default for a worker who has expressed no preference (§8).
 *
 * NOT STORED ANYWHERE YET, and that is a slice boundary rather than an
 * oversight: §8 requires the choice to survive reinstalling the app on a new
 * phone, which means a column and an endpoint, not localStorage. V16 builds the
 * worker app shell and is where that belongs. Until then every caller passes
 * `system` and the surface defaults below decide.
 */
export type ThemePreference = Theme | "system";

/**
 * The portal is warm and light-only. §2 gives it the brand palette on white,
 * and 11-console-design-system.md specifies no dark portal token set — so
 * there is nothing to switch to, and answering "dark" here would resolve to a
 * set that does not exist. Run 12 recorded the missing portal token values as a
 * deviation deferred to V24; this is the seam where that deviation is visible.
 */
const FOLLOWS_DEVICE: Record<Surface, boolean> = {
  console: false,
  portal: false,
  worker: true,
};

const DEFAULT_THEME: Record<Surface, Theme> = {
  console: "dark",
  portal: "light",
  worker: "light",
};

/**
 * @param deviceIsDark what `prefers-color-scheme: dark` reports.
 */
export function themeFor(
  surface: Surface,
  preference: ThemePreference,
  deviceIsDark: boolean,
): Theme {
  // The portal has one palette. An explicit preference cannot select a token
  // set that has not been written, and honouring one here would render the
  // portal in whatever the marketing palette leaves behind.
  if (surface === "portal") return "light";
  if (preference !== "system") return preference;
  if (FOLLOWS_DEVICE[surface]) return deviceIsDark ? "dark" : "light";
  return DEFAULT_THEME[surface];
}

export type Chrome = { surface: Surface; theme: Theme };

export function chromeFor(
  surface: Surface,
  preference: ThemePreference,
  deviceIsDark: boolean,
): Chrome {
  return { surface, theme: themeFor(surface, preference, deviceIsDark) };
}

/** The attribute names, spelled once. surfaces.css is the only other speller. */
export const SURFACE_ATTR = "data-surface";
export const THEME_ATTR = "data-theme";

/**
 * Write the chrome onto an element and return the undo.
 *
 * THE UNDO IS THE POINT. Leaving data-surface on <html> after navigating from
 * /console to /sign-in renders the auth screen — which §2 says must carry the
 * brand — in a dark 13px console palette, and it would do it only on the
 * back-button path that a first load never takes. Returning the teardown rather
 * than expecting a caller to remember it is what makes that impossible to skip.
 *
 * Takes an element rather than reaching for `document` so the behaviour can be
 * asserted against any object with the two methods, with no DOM.
 */
export type AttributeHost = {
  setAttribute(name: string, value: string): void;
  removeAttribute(name: string): void;
};

export function applyChrome(host: AttributeHost, chrome: Chrome): () => void {
  host.setAttribute(SURFACE_ATTR, chrome.surface);
  host.setAttribute(THEME_ATTR, chrome.theme);
  return () => {
    host.removeAttribute(SURFACE_ATTR);
    host.removeAttribute(THEME_ATTR);
  };
}
